<template>
  <section class="invoice-preview-wrapper">
    <b-card no-body>
      <!-- Print Confirmation Modal -->
      <b-modal
        :id="`modal-confirm-print`"
        :no-close-on-backdrop="true"
        :title="$t('surgery.print-invoice')"
        ok-only
        :ok-title="$t('common.print')"
        @ok="printInvoice"
      >
        <b-card-text>
          <h5>{{ $t("surgery.print-invoice") }}</h5>
          {{ $t("surgery.print-sure") }}
        </b-card-text>
      </b-modal>

      <!-- OutClinic Print Confirmation Modal -->
      <b-modal
        :id="`modal-confirm-print-outclinic`"
        :no-close-on-backdrop="true"
        :title="$t('surgery.print-invoice')"
        ok-only
        :ok-title="$t('common.print')"
        @ok="printOutClinicInvoice"
      >
        <b-card-text>
          <h5>{{ $t("surgery.print-invoice") }}</h5>
          {{ $t("surgery.print-sure") }}
        </b-card-text>
      </b-modal>

      <!-- Surgery Deposit Print Confirmation Modal -->
      <b-modal
        :id="`modal-confirm-print-surgery-deposit`"
        :no-close-on-backdrop="true"
        :title="$t('surgery.print-invoice')"
        ok-only
        :ok-title="$t('common.print')"
        @ok="printSurgeryDepositInvoice"
      >
        <b-card-text>
          <h5>{{ $t("surgery.print-invoice") }}</h5>
          {{ $t("surgery.print-sure") }}
        </b-card-text>
      </b-modal>

      <!-- Patient Stay Print Confirmation Modal -->
      <b-modal
        :id="`modal-confirm-print-patient-stay`"
        :no-close-on-backdrop="true"
        :title="$t('surgery.print-invoice')"
        ok-only
        :ok-title="$t('common.print')"
        @ok="printPatientStayInvoice"
      >
        <b-card-text>
          <h5>{{ $t("surgery.print-invoice") }}</h5>
          {{ $t("surgery.print-sure") }}
        </b-card-text>
      </b-modal>

      <b-modal
        id="outClinicInvoicesModal"
        centered
        scrollable
        size="xl"
        :title="$t('hospital.outclinic-invoices')"
        hide-footer
      >
        <b-table
          :items="currentOutClinicInvoices"
          responsive
          :fields="outClinicColumns"
          primary-key="id"
          class="position-relative border-bottom"
          show-empty
          striped
          hover
          empty-text="No invoices found"
        >
          <template #cell(service_title)="data">
            <div>{{ data.item.service ? data.item.service.title : "" }}</div>
          </template>
          <template #cell(amount)="data">
            <div>{{ data.item.amount }}</div>
          </template>
          <template #cell(type)="data">
            <div>{{ data.item.type }}</div>
          </template>
          <template #cell(description)="data">
            <div>{{ data.item.description || "-" }}</div>
          </template>
          <template #cell(status)="data">
            <div>
              <ChangeInvoiceStatusDropDown
                name="outClinicInvoice"
                endpoint="invoices"
                :data="data.item"
                @onEmit="getInvoices"
              />
            </div>
          </template>
          <template #cell(created_at)="data">
            <div>{{ formatDateTime(data.item.created_at) }}</div>
          </template>
          <template #cell(updated_at)="data">
            <div>{{ formatDateTime(data.item.updated_at) }}</div>
          </template>
          <template #cell(date)="data">
            <div>
              {{ data.item.date || formatDateTime(data.item.created_at) }}
            </div>
          </template>
          <template #cell(actions)="data">
            <div
              v-if="data.item.status === 'approved'"
              class="text-nowrap d-flex"
            >
              <b-button
                variant="outline-primary"
                size="sm"
                class="mx-4"
                @click="showOutClinicModal(data.item)"
              >
                {{ $t("reception.Print Invoice") }}
              </b-button>
              <b-button
                ref="outClinicInvoiceRef"
                v-print="OutClinicInvoiceBtn"
                variant="outline-primary"
                size="sm"
                class="hidden"
              >
                {{ $t("reception.Print Invoice") }}
              </b-button>
            </div>
          </template>
        </b-table>
      </b-modal>

      <b-modal
        id="surgeryDepositInvoicesModal"
        centered
        scrollable
        size="xl"
        :title="$t('hospital.surgery-deposit-invoices')"
        hide-footer
      >
        <b-table
          :items="currentSurgeryDepositInvoices"
          responsive
          :fields="surgeryDepositColumns"
          primary-key="id"
          class="position-relative border-bottom"
          show-empty
          striped
          hover
          empty-text="No invoices found"
        >
          <template #cell(amount)="data">
            <div>{{ data.item.amount }}</div>
          </template>
          <template #cell(type)="data">
            <div>{{ data.item.type }}</div>
          </template>
          <template #cell(surgery_id)="data">
            <div>{{ data.item.surgery_id || "-" }}</div>
          </template>
          <template #cell(doctor_id)="data">
            <div>{{ data.item.doctor_id || "-" }}</div>
          </template>
          <template #cell(description)="data">
            <div>{{ data.item.description || "-" }}</div>
          </template>
          <template #cell(status)="data">
            <div>
              <ChangeInvoiceStatusDropDown
                name="surgeryDepositInvoice"
                endpoint="invoices"
                :data="data.item"
                @onEmit="getInvoices"
              />
            </div>
          </template>
          <template #cell(created_at)="data">
            <div>{{ formatDateTime(data.item.created_at) }}</div>
          </template>
          <template #cell(updated_at)="data">
            <div>{{ formatDateTime(data.item.updated_at) }}</div>
          </template>
          <template #cell(date)="data">
            <div>
              {{ data.item.date || formatDateTime(data.item.created_at) }}
            </div>
          </template>
          <template #cell(actions)="data">
            <div
              v-if="data.item.status === 'approved'"
              class="text-nowrap d-flex"
            >
              <b-button
                variant="outline-primary"
                size="sm"
                class="mx-4"
                @click="showSurgeryDepositModal(data.item)"
              >
                {{ $t("reception.Print Invoice") }}
              </b-button>
              <b-button
                ref="surgeryInsuranceInvoiceRef"
                v-print="SurgeryInsuranceInvoiceBtn"
                variant="outline-primary"
                size="sm"
                class="hidden"
              >
                {{ $t("reception.Print Invoice") }}
              </b-button>
            </div>
          </template>
        </b-table>
      </b-modal>

      <b-modal
        id="patientSurgeryInvoicesModal"
        centered
        scrollable
        size="xl"
        :title="$t('hospital.patient-surgery-invoices')"
        hide-footer
      >
        <b-table
          :items="currentPatientSurgeryInvoices"
          responsive
          :fields="patientSurgeryColumns"
          primary-key="id"
          class="position-relative border-bottom"
          show-empty
          striped
          hover
          empty-text="No invoices found"
        >
          <template #cell(total_amount)="data">
            <div>{{ data.item.total_amount }}</div>
          </template>
          <template #cell(hospital_net_amount)="data">
            <div>{{ data.item.hospital_net_amount }}</div>
          </template>
          <template #cell(status)="data">
            <div>
              <ChangeInvoiceStatusDropDown
                name="surgeryInvoice"
                endpoint="invoicePatientSergury"
                :data="data.item"
                @onEmit="getInvoices"
              />
            </div>
          </template>
          <template #cell(room_amount)="data">
            <div>{{ data.item.room_amount }}</div>
          </template>
          <template #cell(surgery_room_amount)="data">
            <div>{{ data.item.surgery_room_amount }}</div>
          </template>
          <template #cell(general_services_amount)="data">
            <div>{{ data.item.general_services_amount }}</div>
          </template>
          <template #cell(nursing_amount)="data">
            <div>{{ data.item.nursing_amount }}</div>
          </template>
          <template #cell(consultation_amount)="data">
            <div>{{ data.item.consultation_amount }}</div>
          </template>
          <template #cell(admin_services_amount)="data">
            <div>{{ data.item.admin_services_amount }}</div>
          </template>
          <template #cell(op_nurse_amount)="data">
            <div>{{ data.item.op_nurse_amount }}</div>
          </template>
          <template #cell(labour_room_amount)="data">
            <div>{{ data.item.labour_room_amount }}</div>
          </template>
          <template #cell(surgeon_amount)="data">
            <div>{{ data.item.surgeon_amount }}</div>
          </template>
          <template #cell(surgeon_assist_amount)="data">
            <div>{{ data.item.surgeon_assist_amount }}</div>
          </template>
          <template #cell(anesthetist_amount)="data">
            <div>{{ data.item.anesthetist_amount }}</div>
          </template>
          <template #cell(anaesthetist_assist_amount)="data">
            <div>{{ data.item.anaesthetist_assist_amount }}</div>
          </template>
          <template #cell(medical_consumables_amount)="data">
            <div>{{ data.item.medical_consumables_amount }}</div>
          </template>
          <template #cell(management_services_amount)="data">
            <div>{{ data.item.management_services_amount }}</div>
          </template>
          <template #cell(surgery_nurse_assist_amount)="data">
            <div>{{ data.item.surgery_nurse_assist_amount }}</div>
          </template>
          <template #cell(delivery_room_amount)="data">
            <div>{{ data.item.delivery_room_amount }}</div>
          </template>
          <template #cell(scanner_device_amount)="data">
            <div>{{ data.item.scanner_device_amount }}</div>
          </template>
          <template #cell(pre_incubator_amount)="data">
            <div>{{ data.item.pre_incubator_amount }}</div>
          </template>
          <template #cell(pre_cpap_amount)="data">
            <div>{{ data.item.pre_cpap_amount }}</div>
          </template>
          <template #cell(pre_ventameter_amount)="data">
            <div>{{ data.item.pre_ventameter_amount }}</div>
          </template>
          <template #cell(pre_test_amount)="data">
            <div>{{ data.item.pre_test_amount }}</div>
          </template>
          <template #cell(tissue_transplant_amount)="data">
            <div>{{ data.item.tissue_transplant_amount }}</div>
          </template>
          <template #cell(created_at)="data">
            <div>{{ formatDateTime(data.item.created_at) }}</div>
          </template>
          <template #cell(date)="data">
            <div>
              {{ data.item.date || formatDateTime(data.item.created_at) }}
            </div>
          </template>
          <template #cell(actions)="data">
            <div
              v-if="data.item.status === 'approved'"
              class="text-nowrap d-flex"
            >
              <b-button
                variant="outline-primary"
                size="sm"
                class="mx-4"
                @click="showModal(data.item)"
              >
                {{ $t("reception.Print Invoice") }}
              </b-button>
              <b-button
                ref="surgeryInvoiceRef"
                v-print="ServicesInvoiceBtn"
                variant="outline-primary"
                size="sm"
                class="hidden"
              >
                {{ $t("reception.Print Invoice") }}
              </b-button>
            </div>
          </template>
        </b-table>
      </b-modal>

      <b-modal
        id="patientStayInvoicesModal"
        centered
        scrollable
        size="xl"
        :title="$t('common.patient-stay-invoices')"
        hide-footer
      >
        <b-table
          :items="currentPatientStayInvoices"
          responsive
          :fields="patientStayColumns"
          primary-key="id"
          class="position-relative border-bottom"
          show-empty
          striped
          hover
          empty-text="No invoices found"
        >
          <template #cell(total_amount)="data">
            <div>{{ data.item.total_amount }}</div>
          </template>
          <template #cell(hospital_net_amount)="data">
            <div>{{ data.item.hospital_net_amount }}</div>
          </template>
          <template #cell(status)="data">
            <div>
              <ChangeInvoiceStatusDropDown
                name="surgeryInvoice"
                endpoint="invoicePatientStay"
                :data="data.item"
                @onEmit="getInvoices"
              />
            </div>
          </template>
          <template #cell(room_amount)="data">
            <div>{{ data.item.room_amount }}</div>
          </template>
          <template #cell(surgery_room_amount)="data">
            <div>{{ data.item.surgery_room_amount }}</div>
          </template>
          <template #cell(general_services_amount)="data">
            <div>{{ data.item.general_services_amount }}</div>
          </template>
          <template #cell(nursing_amount)="data">
            <div>{{ data.item.nursing_amount }}</div>
          </template>
          <template #cell(nurses_amount)="data">
            <div>{{ data.item.nurses_amount }}</div>
          </template>
          <template #cell(consultation_amount)="data">
            <div>{{ data.item.consultation_amount }}</div>
          </template>
          <template #cell(admin_services_amount)="data">
            <div>{{ data.item.admin_services_amount }}</div>
          </template>
          <template #cell(op_nurse_amount)="data">
            <div>{{ data.item.op_nurse_amount }}</div>
          </template>
          <template #cell(labour_room_amount)="data">
            <div>{{ data.item.labour_room_amount }}</div>
          </template>
          <template #cell(doctor_amount)="data">
            <div>{{ data.item.doctor_amount }}</div>
          </template>
          <template #cell(doctor_assist_amount)="data">
            <div>{{ data.item.doctor_assist_amount }}</div>
          </template>
          <template #cell(medical_consumables_amount)="data">
            <div>{{ data.item.medical_consumables_amount }}</div>
          </template>
          <template #cell(management_services_amount)="data">
            <div>{{ data.item.management_services_amount }}</div>
          </template>
          <template #cell(surgery_nurse_assist_amount)="data">
            <div>{{ data.item.surgery_nurse_assist_amount }}</div>
          </template>
          <template #cell(delivery_room_amount)="data">
            <div>{{ data.item.delivery_room_amount }}</div>
          </template>
          <template #cell(scanner_device_amount)="data">
            <div>{{ data.item.scanner_device_amount }}</div>
          </template>
          <template #cell(pre_incubator_amount)="data">
            <div>{{ data.item.pre_incubator_amount }}</div>
          </template>
          <template #cell(pre_cpap_amount)="data">
            <div>{{ data.item.pre_cpap_amount }}</div>
          </template>
          <template #cell(pre_ventameter_amount)="data">
            <div>{{ data.item.pre_ventameter_amount }}</div>
          </template>
          <template #cell(pre_test_amount)="data">
            <div>{{ data.item.pre_test_amount }}</div>
          </template>
          <template #cell(tissue_transplant_amount)="data">
            <div>{{ data.item.tissue_transplant_amount }}</div>
          </template>
          <template #cell(created_at)="data">
            <div>{{ formatDateTime(data.item.created_at) }}</div>
          </template>
          <template #cell(date)="data">
            <div>
              {{ data.item.date || formatDateTime(data.item.created_at) }}
            </div>
          </template>
          <template #cell(actions)="data">
            <div class="text-nowrap d-flex">
              <b-button
                v-if="data.item.status === 'approved'"
                variant="outline-primary"
                size="sm"
                class="mx-4"
                @click="showPatientStayModal(data.item)"
              >
                {{ $t("reception.Print Invoice") }}
              </b-button>
              <b-button
                ref="patientStayInvoiceRef"
                v-print="PatientStayInvoiceBtn"
                variant="outline-primary"
                size="sm"
                class="hidden"
              >
                {{ $t("reception.Print Invoice") }}
              </b-button>
            </div>
          </template>
        </b-table>
      </b-modal>

      <div class="m-2">
        <b-row>
          <b-col md="12">
            <h2>{{ $t("common.patient-history") }}</h2>
          </b-col>
          <b-col cols="12" md="12">
            <br />
          </b-col>
          <b-col md="3" cols="12">
            <!-- <h4>Patient Name</h4> -->
            <b-form-group
              :label="$t('reception.patient-name')"
              label-for="Name"
            >
              <b-form-input
                id="Name"
                v-model="patientName"
                :placeholder="$t('reception.patient-name')"
                @keydown.enter="getInvoices()"
              />
            </b-form-group>
          </b-col>
          <b-col md="3" cols="12">
            <b-form-group
              :label="$t('accounting.invoice_status')"
              label-for="Type"
            >
              <v-select
                v-model="invoice_status"
                :options="['approved', 'pending', 'rejected', 'printed']"
                :placeholder="$t('accounting.invoice_status')"
              />
            </b-form-group>
          </b-col>
          <b-col md="2" cols="12">
            <b-form-group
              :label="$t('common.from-date')"
              label-for="surgery_date"
            >
              <datepicker
                id="surgery_date"
                v-model="surgery_date"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                class="datebtn2"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>
          <!-- <b-col cols="12" md="2">
            <b-form-group
              :label="$t('common.pateint-card-id-status')"
              label-for="Type"
            >
              <v-select
                v-model="active_cards"
                :placeholder="$t('common.pateint-card-id-status')"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="activeListFilterOption"
                label="text"
                :reduce="(status) => status.value"
              />
            </b-form-group>
          </b-col> -->
          <b-col
            cols="12"
            md="2"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <!-- <h4></h4> -->
            <b-button class="mt-50" variant="primary" @click="getInvoices()">
              {{ $t("common.search") }}
              <b-icon-search class="ml-1" />
            </b-button>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col>
            <b-table
              v-if="!loading"
              :items="patientCardsData"
              responsive
              :fields="tableColumns"
              primary-key="id"
              class="position-relative"
              show-empty
              empty-text="No matching patients found"
            >
              <template #cell(phone)="data">
                <div
                  style="unicode-bidi: isolate; text-wrap: nowrap"
                  :style="{
                    direction: $store.state.appConfig.isRTL ? 'rtl' : 'ltr',
                  }"
                >
                  {{ data.item.phone }}
                </div>
              </template>
              <template #cell(out-clinic)="data">
                <div class="d-flex align-items-center justify-content-between">
                  <b-badge variant="light-primary" class="mr-1">
                    {{ data.item.invoice_out_clinic.length }}
                  </b-badge>
                  <b-button
                    v-if="data.item.invoice_out_clinic.length > 0"
                    size="sm"
                    variant="outline-primary"
                    class="btn-icon"
                    @click="showOutClinicInvoices(data.item.invoice_out_clinic)"
                  >
                    <span class="mr-1">{{ $t("common.view") }}</span>
                    <feather-icon icon="EyeIcon" size="16" />
                  </b-button>
                </div>
              </template>
              <template #cell(surgery-deposit-invoices)="data">
                <div class="d-flex align-items-center justify-content-between">
                  <b-badge variant="light-primary" class="mr-1">
                    {{ data.item.invoice_insurance_surgery.length }}
                  </b-badge>
                  <b-button
                    v-if="data.item.invoice_insurance_surgery.length > 0"
                    size="sm"
                    variant="outline-primary"
                    class="btn-icon"
                    @click="
                      showSurgeryDepositInvoices(
                        data.item.invoice_insurance_surgery
                      )
                    "
                  >
                    <span class="mr-1">{{ $t("common.view") }}</span>
                    <feather-icon icon="EyeIcon" size="16" />
                  </b-button>
                </div>
              </template>
              <template #cell(patient-surgery-invoices)="data">
                <div class="d-flex align-items-center justify-content-between">
                  <b-badge variant="light-primary" class="mr-1">
                    {{ data.item.invoice_patient_surgery.length }}
                  </b-badge>
                  <b-button
                    v-if="data.item.invoice_patient_surgery.length > 0"
                    size="sm"
                    variant="outline-primary"
                    class="btn-icon"
                    @click="
                      showPatientSurgeryInvoices(
                        data.item.invoice_patient_surgery
                      )
                    "
                  >
                    <span class="mr-1">{{ $t("common.view") }}</span>
                    <feather-icon icon="EyeIcon" size="16" />
                  </b-button>
                </div>
              </template>
              <template #cell(patient-stay-invoices)="data">
                <div class="d-flex align-items-center justify-content-between">
                  <b-badge variant="light-primary" class="mr-1">
                    {{ data.item.invoice_patient_stay.length }}
                  </b-badge>
                  <b-button
                    v-if="data.item.invoice_patient_stay.length > 0"
                    size="sm"
                    variant="outline-primary"
                    class="btn-icon"
                    @click="
                      showPatientStayInvoices(data.item.invoice_patient_stay)
                    "
                  >
                    <span class="mr-1">{{ $t("common.view") }}</span>
                    <feather-icon icon="EyeIcon" size="16" />
                  </b-button>
                </div>
              </template>
              <template #cell(is_active)="data">
                <b-badge
                  :variant="data.item.patient_card ? 'success' : 'danger'"
                >
                  {{ data.item.patient_card ? "Active" : "Not Active" }}
                </b-badge>
              </template>
              <template #cell(actions)="data">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="btn-icon"
                  @click="viewPatientInvoices(data.item.id)"
                >
                  <span class="mr-1">{{ $t("common.view") }}</span>
                  <feather-icon icon="EyeIcon" size="16" />
                </b-button>
              </template>
            </b-table>
            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner label="Loading..." />
                </div>
              </div>
            </template>
          </b-col>
        </b-row>
      </div>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >{{ $t("common.showing") }} 1 {{ $t("common.to") }} 10
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-if="paginationReady"
              :key="`pagination-${page}-${paginationReady}`"
              v-model="page"
              :total-rows="dataCount"
              :per-page="take"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <ServicesInvoice :patient-data="patientData" :data="invoice" />
    <OutClinicInvoice :patient-data="patientData" :data="invoice" />
    <SurgeryInsuranceInvoice :patient-data="patientData" :data="invoice" />
    <ServicesInvoiceStay :patient-data="patientData" :data="invoice" />
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTable,
  BCardBody,
  BTableLite,
  BCardText,
  BButton,
  BAlert,
  BLink,
  BFormInput,
  BMedia,
  BAvatar,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
  BFormGroup,
  BFormSelect,
  BSpinner,
  BIconSearch,
  BModal,
} from "bootstrap-vue";
import vSelect from "vue-select";
import PatientSearch from "@/components/patientSearch.vue";
import Datepicker from "vuejs-datepicker";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

const ServicesInvoice = () =>
  import("@/components/printables/ServicesInvoice.vue");
const OutClinicInvoice = () =>
  import("@/components/printables/OutClinicInvoice.vue");
const SurgeryInsuranceInvoice = () =>
  import("@/components/printables/SurgeryInsuranceInvoice.vue");
const ChangeInvoiceStatusDropDown = () =>
  import("@/components/invoices/ChangeInvoiceStatusDropDown.vue");
const ServicesInvoiceStay = () =>
  import("@/components/printables/ServicesInvoiceStay.vue");

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BCard,
    BCardBody,
    BTableLite,
    BCardText,
    BButton,
    BAlert,
    BLink,
    vSelect,
    BFormInput,
    BMedia,
    BAvatar,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,
    BFormGroup,
    BFormSelect,
    BSpinner,
    BModal,
    BIconSearch,
    PatientSearch,
    Datepicker,
    ServicesInvoice,
    OutClinicInvoice,
    SurgeryInsuranceInvoice,
    ChangeInvoiceStatusDropDown,
    ServicesInvoiceStay,
  },
  data() {
    return {
      tableColumns: [
        {
          key: "name",
          label: this.$t("common.name"),
          sortable: true,
          class: "text-nowrap",
        },
        {
          key: "patient_no",
          label: this.$t("accounting.patient_no"),
          sortable: true,
        },
        {
          key: "birth_date",
          label: this.$t("reception.birth-date"),
          sortable: true,
          class: "text-nowrap",
        },
        {
          key: "phone",
          label: this.$t("reception.phone"),
          sortable: true,
          class: "text-nowrap",
        },
        {
          key: "is_active",
          label: this.$t("common.status"),
          sortable: true,
        },

        // {
        //   key: "out-clinic",
        //   label: this.$t("hospital.outclinic-invoices"),
        //   sortable: true,
        // },
        // {
        //   key: "surgery-deposit-invoices",
        //   label: this.$t("hospital.surgery-deposit-invoices"),
        //   sortable: true,
        // },
        // {
        //   key: "patient-surgery-invoices",
        //   label: this.$t("hospital.patient-surgery-invoices"),
        //   sortable: true,
        // },
        // {
        //   key: "patient-stay-invoices",
        //   label: this.$t("common.patient-stay-invoices"),
        //   sortable: true,
        // },
        { key: "actions", label: this.$t("common.actions") },
      ],

      // Define table columns for each invoice type
      outClinicColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        { key: "amount", label: this.$t("accounting.amount"), sortable: true },
        {
          key: "service_title",
          label: this.$t("hospital.service"),
          sortable: true,
        },
        // { key: 'type', label: this.$t('common.type'), sortable: true },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "actions", label: this.$t("common.actions") },
        {
          key: "description",
          label: this.$t("common.description"),
          sortable: true,
        },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created-at"),
          sortable: true,
        },
        {
          key: "updated_at",
          label: this.$t("common.updated-at"),
          sortable: true,
        },
      ],

      surgeryDepositColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        { key: "amount", label: this.$t("accounting.amount"), sortable: true },
        // { key: 'type', label: this.$t('common.type'), sortable: true },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "actions", label: this.$t("common.actions") },
        // { key: 'surgery_id', label: this.$t('hospital.surgery'), sortable: true },
        // { key: 'doctor_id', label: this.$t('reception.doctor'), sortable: true },
        {
          key: "description",
          label: this.$t("common.description"),
          sortable: true,
        },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created_at"),
          sortable: true,
        },
        {
          key: "updated_at",
          label: this.$t("common.updated-at"),
          sortable: true,
        },
      ],

      patientSurgeryColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        {
          key: "total_amount",
          label: this.$t("accounting.total_amount"),
          sortable: true,
        },
        {
          key: "hospital_net_amount",
          label: this.$t("accounting.hospital_net_amount"),
          sortable: true,
        },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "actions", label: this.$t("common.actions") },
        {
          key: "room_amount",
          label: this.$t("accounting.room_amount"),
          sortable: true,
        },
        {
          key: "surgery_room_amount",
          label: this.$t("accounting.surgery_room_amount"),
          sortable: true,
        },
        {
          key: "general_services_amount",
          label: this.$t("accounting.general_services_amount"),
          sortable: true,
        },
        {
          key: "nursing_amount",
          label: this.$t("accounting.nursing_amount"),
          sortable: true,
        },
        {
          key: "consultation_amount",
          label: this.$t("accounting.consultation_amount"),
          sortable: true,
        },
        {
          key: "admin_services_amount",
          label: this.$t("accounting.admin_services_amount"),
          sortable: true,
        },
        {
          key: "op_nurse_amount",
          label: this.$t("accounting.op_nurse_amount"),
          sortable: true,
        },
        {
          key: "labour_room_amount",
          label: this.$t("accounting.labour_room_amount"),
          sortable: true,
        },
        {
          key: "surgeon_amount",
          label: this.$t("accounting.surgeon_amount"),
          sortable: true,
        },
        {
          key: "surgeon_assist_amount",
          label: this.$t("accounting.surgeon_assist_amount"),
          sortable: true,
        },
        {
          key: "anesthetist_amount",
          label: this.$t("accounting.anesthetist_amount"),
          sortable: true,
        },
        {
          key: "anaesthetist_assist_amount",
          label: this.$t("accounting.anaesthetist_assist_amount"),
          sortable: true,
        },
        {
          key: "medical_consumables_amount",
          label: this.$t("accounting.medical_consumables_amount"),
          sortable: true,
        },
        // { key: 'management_services_amount', label: this.$t('accounting.management_services_amount'), sortable: true },
        {
          key: "surgery_nurse_assist_amount",
          label: this.$t("accounting.surgery_nurse_assist_amount"),
          sortable: true,
        },
        // { key: 'delivery_room_amount', label: this.$t('accounting.delivery_room_amount'), sortable: true },
        {
          key: "scanner_device_amount",
          label: this.$t("accounting.scanner_device_amount"),
          sortable: true,
        },
        {
          key: "pre_incubator_amount",
          label: this.$t("accounting.pre_incubator_amount"),
          sortable: true,
        },
        {
          key: "pre_cpap_amount",
          label: this.$t("accounting.pre_cpap_amount"),
          sortable: true,
        },
        // { key: 'pre_ventameter_amount', label: this.$t('accounting.pre_ventameter_amount'), sortable: true },
        {
          key: "pre_test_amount",
          label: this.$t("accounting.pre_test_amount"),
          sortable: true,
        },
        {
          key: "tissue_transplant_amount",
          label: this.$t("accounting.tissue_transplant_amount"),
          sortable: true,
        },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created_at"),
          sortable: true,
        },
        {
          key: "updated_at",
          label: this.$t("common.updated-at"),
          sortable: true,
        },
      ],

      patientStayColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        {
          key: "total_amount",
          label: this.$t("accounting.total_amount"),
          sortable: true,
        },
        {
          key: "hospital_net_amount",
          label: this.$t("accounting.hospital_net_amount"),
          sortable: true,
        },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "actions", label: this.$t("common.actions") },
        {
          key: "room_amount",
          label: this.$t("accounting.room_amount"),
          sortable: true,
        },
        // { key: 'surgery_room_amount', label: this.$t('accounting.surgery_room_amount'), sortable: true },
        {
          key: "general_services_amount",
          label: this.$t("accounting.general_services_amount"),
          sortable: true,
        },
        {
          key: "nursing_amount",
          label: this.$t("accounting.nursing_amount"),
          sortable: true,
        },
        // { key: 'nurses_amount', label: this.$t('accounting.nurses_amount'), sortable: true },
        {
          key: "consultation_amount",
          label: this.$t("accounting.consultation_amount"),
          sortable: true,
        },
        {
          key: "admin_services_amount",
          label: this.$t("accounting.admin_services_amount"),
          sortable: true,
        },
        {
          key: "op_nurse_amount",
          label: this.$t("accounting.op_nurse_amount"),
          sortable: true,
        },
        {
          key: "labour_room_amount",
          label: this.$t("accounting.labour_room_amount"),
          sortable: true,
        },
        {
          key: "doctor_amount",
          label: this.$t("accounting.doctor_amount"),
          sortable: true,
        },
        {
          key: "doctor_assist_amount",
          label: this.$t("accounting.doctor_assist_amount"),
          sortable: true,
        },
        {
          key: "medical_consumables_amount",
          label: this.$t("accounting.medical_consumables_amount"),
          sortable: true,
        },
        {
          key: "management_services_amount",
          label: this.$t("accounting.management_services_amount"),
          sortable: true,
        },
        {
          key: "surgery_nurse_assist_amount",
          label: this.$t("accounting.surgery_nurse_assist_amount"),
          sortable: true,
        },
        {
          key: "delivery_room_amount",
          label: this.$t("accounting.delivery_room_amount"),
          sortable: true,
        },
        {
          key: "scanner_device_amount",
          label: this.$t("accounting.scanner_device_amount"),
          sortable: true,
        },
        {
          key: "pre_incubator_amount",
          label: this.$t("accounting.pre_incubator_amount"),
          sortable: true,
        },
        {
          key: "pre_cpap_amount",
          label: this.$t("accounting.pre_cpap_amount"),
          sortable: true,
        },
        {
          key: "pre_ventameter_amount",
          label: this.$t("accounting.pre_ventameter_amount"),
          sortable: true,
        },
        {
          key: "pre_test_amount",
          label: this.$t("accounting.pre_test_amount"),
          sortable: true,
        },
        {
          key: "tissue_transplant_amount",
          label: this.$t("accounting.tissue_transplant_amount"),
          sortable: true,
        },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created_at"),
          sortable: true,
        },
        {
          key: "updated_at",
          label: this.$t("common.updated-at"),
          sortable: true,
        },
      ],

      // Data for each modal's table
      currentOutClinicInvoices: [],
      currentSurgeryDepositInvoices: [],
      currentPatientSurgeryInvoices: [],
      currentPatientStayInvoices: [],

      patientCardsData: [],
      activeListFilterOption: [
        { text: "Not Active", value: 0 },
        { text: "Active", value: 1 },
      ],
      surgery_date: "",
      patientName: "",
      dataCount: 0,
      invoice_status: "",
      page: 1,
      totalRows: "",
      todayDate: "",
      take: 10,
      loading: false,
      selected: "",
      option: [],
      active_cards: null,
      invoice: {},
      patientData: {
        patient_cards: [],
      },
      surgeryName: "",
      ServicesInvoiceBtn: {
        id: "ServicesInvoice",
      },
      OutClinicInvoiceBtn: {
        id: "OutClinicInvoice",
      },
      SurgeryInsuranceInvoiceBtn: {
        id: "SurgeryInsuranceInvoice",
      },
      PatientStayInvoiceBtn: {
        id: "ServicesInvoiceStay",
      },
      params: {
        form: {},
        path: "",
      },
      selectedSurgeryDepositItem: null,
      paginationReady: false,
      isInitializing: true,
    };
  },
  watch: {
    // Comment #32: Watch for changes in search fields and pagination to save state
    patientName() {
      if (!this.isInitializing) {
        this.saveSearchState();
      }
    },
    invoice_status() {
      if (!this.isInitializing) {
        this.saveSearchState();
      }
    },
    surgery_date() {
      if (!this.isInitializing) {
        this.saveSearchState();
      }
    },
    page() {
      if (!this.isInitializing) {
        this.saveSearchState();
        this.getInvoices();
      }
    },
    take() {
      if (!this.isInitializing) {
        this.saveSearchState();
        this.getInvoices();
      }
    },
  },

  mounted() {
    // Comment #27: Restore search fields and pagination from localStorage
    this.restoreSearchState();
    // Comment #36: Use nextTick to ensure pagination UI is properly updated
    this.$nextTick(() => {
      this.getInvoices();
    });
  },
  methods: {
    // Methods to show each modal with its invoices
    showOutClinicInvoices(invoices) {
      this.currentOutClinicInvoices = invoices;
      this.$bvModal.show("outClinicInvoicesModal");
    },

    showSurgeryDepositInvoices(invoices) {
      this.currentSurgeryDepositInvoices = invoices;
      this.$bvModal.show("surgeryDepositInvoicesModal");
    },

    showPatientSurgeryInvoices(invoices) {
      this.currentPatientSurgeryInvoices = invoices;
      this.$bvModal.show("patientSurgeryInvoicesModal");
    },

    showPatientStayInvoices(invoices) {
      this.currentPatientStayInvoices = invoices;
      this.$bvModal.show("patientStayInvoicesModal");
    },

    // Format datetime for display
    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return "";
      const date = new Date(dateTimeStr);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },

    // Get appropriate color variant for status
    getStatusVariant(status) {
      if (!status) return "secondary";

      switch (status.toLowerCase()) {
        case "approved":
          return "success";
        case "pending":
          return "warning";
        case "rejected":
          return "danger";
        case "printed":
          return "info";
        default:
          return "secondary";
      }
    },

    // this is trmporary
    formatDate(wantedDate) {
      const date = new Date(wantedDate);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },

    // Comment #29: Save current search state to localStorage
    saveSearchState() {
      const searchState = {
        patientName: this.patientName,
        invoice_status: this.invoice_status,
        surgery_date: this.surgery_date,
        page: this.page,
        take: this.take,
      };
      localStorage.setItem(
        "patientInvoicesAll-search-state",
        JSON.stringify(searchState)
      );
    },

    // Comment #30: Restore search state from localStorage
    restoreSearchState() {
      const savedState = localStorage.getItem(
        "patientInvoicesAll-search-state"
      );
      if (savedState) {
        try {
          const searchState = JSON.parse(savedState);
          this.patientName = searchState.patientName || "";
          this.invoice_status = searchState.invoice_status || "";
          this.surgery_date = searchState.surgery_date || "";
          // Comment #38: Ensure page and take are numbers for proper pagination UI sync
          this.page = Number(searchState.page) || 1;
          this.take = Number(searchState.take) || 10;
          console.log("Restored page:", this.page, "take:", this.take);
        } catch (error) {
          console.error("Error parsing saved search state:", error);
        }
      }
      // Comment #42: Set pagination ready flag after state restoration
      this.paginationReady = true;

      // Comment #49: Add delay to ensure pagination component properly syncs
      setTimeout(() => {
        this.isInitializing = false;
        console.log("Initialization complete, page should be:", this.page);
      }, 200);
    },

    async getInvoices() {
      this.loading = true;
      let url = `/patient/invoices?page=${this.page}&take=${this.take}&sort=-id&include=patientCard`;

      if (this.patientName) {
        url += `&filter[name]=${this.patientName}`;
      }
      if (this.surgery_date) {
        url += `&date=${this.formatDate(this.surgery_date)}`;
      }
      if (this.invoice_status) {
        url += `&status=${this.invoice_status}`;
      }
      // if (typeof this.active_cards == 'number') {
      //   url += `&active_cards=${this.active_cards}`;
      // }
      await this.getList(url);
      for (const item in this.all_data) {
        if (this.all_data[item] == null) this.all_data[item] = "";
      }
      this.dataCount = this.all_data.total;
      this.page = this.all_data.current_page;
      this.patientCardsData = this.all_data.data;
      this.loading = false;
      this.saveSearchState();
    },

    async printInvoice() {
      this.$refs.surgeryInvoiceRef.click();
      this.changeStatusToPrinted();
    },

    async printOutClinicInvoice() {
      this.$refs.outClinicInvoiceRef.click();
      this.changeStatusToPrintedInvoices();
    },

    async printSurgeryDepositInvoice() {
      try {
        this.loading = true;

        // Fetch detailed invoice data from API
        const url = `/invoices?filter[id]=${this.selectedSurgeryDepositItem.id}&include=service,surgery,patientCard,doctor`;
        await this.getList(url);

        if (
          this.all_data &&
          this.all_data.data &&
          this.all_data.data.length > 0
        ) {
          // Set the detailed invoice data
          this.invoice = this.all_data.data[0];

          // Wait for Vue to update the DOM with the new data
          await this.$nextTick();

          // Add a small delay to ensure all data is properly rendered
          setTimeout(() => {
            // Trigger the print using v-print directive
            this.$refs.surgeryInsuranceInvoiceRef.click();
            this.changeStatusToPrintedInvoices();
          }, 100);
        }
      } catch (error) {
        console.error("Failed to print surgery deposit invoice:", error);
      } finally {
        this.loading = false;
      }
    },

    async printPatientStayInvoice() {
      this.$refs.patientStayInvoiceRef.click();
      this.changeStatusToPrintedPatientStay();
    },

    async showOutClinicModal(status) {
      this.invoice = status;
      this.patientData = {
        name: status.patient ? status.patient.name : "",
        patient_cards: [],
      };
      console.log("outClinic invoice", this.invoice);
      console.log("outClinic patientData", this.patientData);
      this.$bvModal.show("modal-confirm-print-outclinic");
    },

    async showSurgeryDepositModal(status) {
      // Store the item for later use
      this.selectedSurgeryDepositItem = status;
      console.log(
        "Selected surgery deposit invoice item:",
        this.selectedSurgeryDepositItem
      );
      this.$bvModal.show("modal-confirm-print-surgery-deposit");
    },

    async showPatientStayModal(status) {
      this.invoice = status;
      this.patientData = {
        patient: {
          name: this.invoice.patient_card.patient.name,
        },
        created_at: this.invoice.patient_card.created_at,
        out_date_time: this.invoice.patient_card.out_date_time,
      };
      console.log("Patient stay invoice", this.invoice);
      console.log("Patient stay patientData", this.patientData);
      this.$bvModal.show("modal-confirm-print-patient-stay");
    },

    async showModal(status) {
      this.invoice = status;
      this.invoice.surgery_name =
        this.invoice.patient_card_surgery.surgery.title;
      this.patientData = {
        name: this.invoice.patient_card.patient.name,
        patient_cards: [this.invoice.patient_card],
      };
      console.log("invoice", this.invoice);
      console.log("patientData", this.patientData);
      this.$bvModal.show("modal-confirm-print");
    },

    async getSurgeryName(surgeryId) {
      await this.getList(`/surgery/${surgeryId}`);
      this.surgeryName = this.all_data.title;
    },
    changeStatusToPrinted() {
      console.log("changeStatusToPrinted");
      (this.params.form = { status: "printed" }),
        (this.params.path = `/invoicePatientSergury/${this.invoice.id}/changeStatus`),
        this.$store
          .dispatch("put", this.params)
          .then((response) => {
            this.getInvoices();
            this.$bvModal.hide("patientSurgeryInvoicesModal");
            this.$bvModal.hide("surgeryDepositInvoicesModal");
            this.$bvModal.hide("outClinicInvoicesModal");
            this.$bvModal.hide("patientStayInvoicesModal");
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: `${this.$t("common.success")}`,
                variant: "success",
                text: `${response.data.message}`,
              },
            });
          })
          .catch((error) => {
            if (error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "validation error",
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
    },
    changeStatusToPrintedInvoices() {
      console.log("changeStatusToPrintedInvoices");
      (this.params.form = { status: "printed" }),
        (this.params.path = `/invoices/${this.invoice.id}/changeStatus`),
        this.$store
          .dispatch("put", this.params)
          .then((response) => {
            this.getInvoices();
            this.$bvModal.hide("patientSurgeryInvoicesModal");
            this.$bvModal.hide("surgeryDepositInvoicesModal");
            this.$bvModal.hide("outClinicInvoicesModal");
            this.$bvModal.hide("patientStayInvoicesModal");
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: `${this.$t("common.success")}`,
                variant: "success",
                text: `${response.data.message}`,
              },
            });
          })
          .catch((error) => {
            if (error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "validation error",
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
    },
    changeStatusToPrintedPatientStay() {
      console.log("changeStatusToPrintedPatientStay");
      (this.params.form = { status: "printed" }),
        (this.params.path = `/invoicePatientStay/${this.invoice.id}/changeStatus`),
        this.$store
          .dispatch("put", this.params)
          .then((response) => {
            this.getInvoices();
            this.$bvModal.hide("patientSurgeryInvoicesModal");
            this.$bvModal.hide("surgeryDepositInvoicesModal");
            this.$bvModal.hide("outClinicInvoicesModal");
            this.$bvModal.hide("patientStayInvoicesModal");
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: `${this.$t("common.success")}`,
                variant: "success",
                text: `${response.data.message}`,
              },
            });
          })
          .catch((error) => {
            if (error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "validation error",
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
    },
    viewPatientInvoices(patientId) {
      this.$router.push({
        name: "patient-invoices-detail",
        params: { patientId },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}

#modal-add___BV_modal_content_ {
  width: 100% !important;
}

#modal-add {
  margin-left: -8% !important;
}

// Add custom styles for improved UI
.btn-icon {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-1px);
  }
}

.table {
  th {
    font-weight: 600;
    background-color: #f8f8f8;
    white-space: nowrap;
  }

  td {
    vertical-align: middle;
  }

  &.table-hover tbody tr:hover {
    background-color: #f6f9fc;
  }
}

.badge {
  padding: 0.5em 0.75em;
  font-weight: 500;
}

.modal-header {
  background-color: #f8f8f8;
  border-bottom: 1px solid #eee;
}

.modal-body {
  padding: 1.5rem;
}

// Responsive improvements
@media (max-width: 768px) {
  .table {
    th,
    td {
      min-width: 120px;
    }
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
